import { CheckIcon } from '@chakra-ui/icons';
import {
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Box,
  Divider,
  Heading,
  List,
  ListIcon,
  ListItem,
  Text,
  VStack,
} from '@chakra-ui/react';
import React from 'react';

const CookiePolicyPage: React.FC = () => {
  return (
    <Box maxW="4xl" mx="auto" p={6}>
      <VStack spacing={6} align="stretch">
        {/* Header */}
        <Box>
          <Heading size="lg" mb={2}>
            Data Storage Policy
          </Heading>
          <Text color="gray.600">
            Last updated: {new Date().toLocaleDateString()}
          </Text>
        </Box>

        {/* Important Notice */}
        <Alert status="success" borderRadius="md">
          <AlertIcon />
          <Box>
            <AlertTitle>
              Perfect for Teams That Clear Browser History!
            </AlertTitle>
            <AlertDescription>
              We don't use cookies for your settings. Everything
              important is tied to your user account, so you can clear
              your browser history daily/weekly without losing any
              features or functionality.
            </AlertDescription>
          </Box>
        </Alert>

        {/* Introduction */}
        <Box>
          <Heading size="md" mb={3}>
            1. Simple Data Storage
          </Heading>
          <Text>
            We save your settings on your computer only. No
            complicated cookie stuff - just simple storage that works
            even when you clear your browser regularly.
          </Text>
        </Box>

        {/* What We Store */}
        <Box>
          <Heading size="md" mb={3}>
            2. What We Save on Your Computer
          </Heading>
          <Text mb={3}>
            We save these simple things on your computer:
          </Text>
          <List spacing={2} ml={4}>
            <ListItem>
              <ListIcon as={CheckIcon} color="green.500" />
              Your app settings and preferences
            </ListItem>
            <ListItem>
              <ListIcon as={CheckIcon} color="green.500" />
              Your login session (so you stay logged in)
            </ListItem>
          </List>
          <Text
            mt={3}
            p={3}
            bg="green.50"
            borderRadius="md"
            borderLeft="4px solid"
            borderLeftColor="green.400"
          >
            <strong>
              Great news for teams that clear browser data:
            </strong>{' '}
            Your important features (like AI Classification) are
            controlled by your user role, not by saved settings. Clear
            your browser as often as you want - your features will
            always be there!
          </Text>
        </Box>

        {/* Types of Cookies */}
        {/* <Box>
          <Heading size="md" mb={3}>
            3. Types of Cookies We Use
          </Heading>

          <Table variant="simple" size="sm">
            <Thead>
              <Tr>
                <Th>Cookie Type</Th>
                <Th>Purpose</Th>
                <Th>Duration</Th>
                <Th>Required</Th>
              </Tr>
            </Thead>
            <Tbody>
              <Tr>
                <Td>
                  <Code>accessToken</Code>
                </Td>
                <Td>User authentication</Td>
                <Td>Session</Td>
                <Td>Yes</Td>
              </Tr>
              <Tr>
                <Td>
                  <Code>cookie_consent</Code>
                </Td>
                <Td>Remember consent choice</Td>
                <Td>1 year</Td>
                <Td>Yes</Td>
              </Tr>
              <Tr>
                <Td>
                  <Code>app_settings</Code>
                </Td>
                <Td>User preferences</Td>
                <Td>1 year</Td>
                <Td>No</Td>
              </Tr>
              <Tr>
                <Td>
                  <Code>customerId</Code>
                </Td>
                <Td>Client identification</Td>
                <Td>Session</Td>
                <Td>Yes</Td>
              </Tr>
            </Tbody>
          </Table>
        </Box> */}

        {/* Cookie Details */}
        {/* <Box>
          <Heading size="md" mb={3}>
            4. Detailed Cookie Information
          </Heading>

          <Box mb={4}>
            <Heading size="sm" mb={2}>
              Essential Cookies
            </Heading>
            <Text mb={2}>
              These cookies are necessary for the application to
              function properly:
            </Text>
            <List spacing={1} ml={4}>
              <ListItem>
                <ListIcon as={WarningIcon} color="orange.500" />
                <strong>accessToken:</strong> Stores your
                authentication token
              </ListItem>
              <ListItem>
                <ListIcon as={WarningIcon} color="orange.500" />
                <strong>customerId:</strong> Identifies your
                organization
              </ListItem>
              <ListItem>
                <ListIcon as={WarningIcon} color="orange.500" />
                <strong>cookie_consent:</strong> Remembers your
                consent choice
              </ListItem>
            </List>
          </Box>

          <Box mb={4}>
            <Heading size="sm" mb={2}>
              Preference Cookies
            </Heading>
            <Text mb={2}>
              These cookies store your personal settings:
            </Text>
            <List spacing={1} ml={4}>
              <ListItem>
                <ListIcon as={InfoIcon} color="blue.500" />
                <strong>app_settings:</strong> Theme, beta features,
                accessibility preferences
              </ListItem>
            </List>
          </Box>
        </Box> */}

        {/* Third-Party Cookies */}
        {/* <Box>
          <Heading size="md" mb={3}>
            5. Third-Party Cookies
          </Heading>
          <Text>
            We may use third-party services that set their own
            cookies. These include:
          </Text>
          <List spacing={2} ml={4} mt={2}>
            <ListItem>
              <ListIcon as={InfoIcon} color="blue.500" />
              <strong>Authentication Service:</strong> Auth0 for user
              login and security
            </ListItem>
          </List>
        </Box> */}

        {/* Managing localStorage */}
        <Box>
          <Heading size="md" mb={3}>
            3. You Control Everything
          </Heading>
          <Text mb={3}>You have complete control:</Text>

          <Heading size="sm" mb={2}>
            3.1 In Your Browser
          </Heading>
          <Text mb={2}>You can:</Text>
          <List spacing={1} ml={4}>
            <ListItem>
              <ListIcon as={CheckIcon} color="green.500" />
              Clear all your browser data anytime
            </ListItem>
            <ListItem>
              <ListIcon as={CheckIcon} color="green.500" />
              See what data is stored
            </ListItem>
          </List>

          <Heading size="sm" mb={2} mt={4}>
            3.2 In The App
          </Heading>
          <Text>
            Change your settings anytime in the Settings page.
            Everything saves automatically - no permission popups or
            cookie consent needed!
          </Text>
        </Box>

        {/* Benefits for Teams */}
        <Alert status="info" borderRadius="md">
          <AlertIcon />
          <Box>
            <AlertTitle>
              Perfect for Teams That Clear Browser Data Daily/Weekly
            </AlertTitle>
            <AlertDescription>
              Your important features (AI Classification, etc.) are
              tied to your user account, not to saved browser data. So
              go ahead and clear your browser history, cookies, and
              cache as often as your team policy requires - everything
              will still work perfectly!
            </AlertDescription>
          </Box>
        </Alert>

        {/* Contact Information */}
        <Box>
          <Heading size="md" mb={3}>
            4. Contact Us
          </Heading>
          <Text>
            If you have questions about our data storage practices,
            please contact us:
          </Text>
          <Box mt={3} p={4} bg="gray.50" borderRadius="md">
            <Text>
              <strong>Denim Health</strong>
              <br />
              Email: support@denimhealth.com
              <br />
            </Text>
          </Box>
        </Box>

        <Divider />

        {/* Footer */}
        <Box textAlign="center" color="gray.600">
          <Text fontSize="sm">
            This data storage policy is effective as of{' '}
            {new Date().toLocaleDateString()} and may be updated
            periodically.
          </Text>
        </Box>
      </VStack>
    </Box>
  );
};

export default CookiePolicyPage;
